import { defineStore } from "pinia";
import { db, uploadImage } from "@/mixins/firebase";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { Install } from "./mimboxes";

export interface WashingMachine {
  id: string;
  brand: string;
  model?: string;
  type?: string;
  year?: number;
  customer?: string;
  images?: string[];
  createdAt?: number;
  updatedAt?: number;
  connected?: string;
  install?: Install;
}

interface BrandData {
  machines: WashingMachine[];
  models: string[];
  types: string[];
  years: number[];
}

export const useWashingMachinesStore = defineStore("washingmachines", {
  state: () => {
    return {
      washingMachines: [] as WashingMachine[],
      selectedWashingMachine: null as WashingMachine | null,
      brands: [] as { name: string; models: string[]; types: string[] }[],
      models: [] as string[],
      types: [] as string[],
      totalCount: 0,
      unsubscribe: null as (() => void) | null,
    };
  },
  actions: {
    async refresh() {
      // If there's an existing subscription, unsubscribe from it
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }

      // Set up real-time listener for washing machines
      const washingMachinesQuery = query(collection(db, "washingmachines"));
      this.unsubscribe = onSnapshot(washingMachinesQuery, (snapshot) => {
        const washingMachines: WashingMachine[] = [];

        snapshot.docs.forEach((brandDoc) => {
          const brandData = brandDoc.data();
          if (brandData.machines) {
            brandData.machines.forEach((machine: WashingMachine) => {
              washingMachines.push({
                ...machine,
                brand: brandDoc.id,
              });
            });
          }
        });

        this.getBrandsModelsTypes(snapshot);
        this.setWashingMachines(washingMachines);
      });
    },
    setWashingMachines(washingMachines: WashingMachine[]) {
      this.washingMachines = washingMachines;
      this.totalCount = washingMachines.length;
    },
    async getWashingMachines() {
      const washingMachinesQuery = query(collection(db, "washingmachines"));
      const washingMachinesSnapshot = await getDocs(washingMachinesQuery);
      const washingMachines: WashingMachine[] = [];
      washingMachinesSnapshot.docs.forEach((brandDoc) => {
        const brandData = brandDoc.data();
        if (brandData.machines) {
          brandData.machines.forEach((machine: WashingMachine) => {
            washingMachines.push({
              ...machine,
              brand: brandDoc.id,
            });
          });
        }
      });
      return washingMachines;
    },
    async selectWashingMachine(id: string) {
      if (this.selectedWashingMachine && this.selectedWashingMachine.id === id) return;
      if (this.washingMachines.length === 0) this.washingMachines = await this.getWashingMachines();
      this.selectedWashingMachine = this.washingMachines.filter((w) => w.id === id)[0];
      if (!this.selectedWashingMachine) throw { message: "This washing machine does not exist" };
    },
    clearSelectedWashingMachine() {
      this.selectedWashingMachine = null;
    },
    async handleImageUploads(
      files: File[] | string[],
      brand: string,
      machineId: string,
    ): Promise<string[]> {
      if (!files) return [];
      return await Promise.all(
        files.map(async (image: File | string) => {
          // If image is already a string (URL), return it directly
          if (typeof image === "string") {
            return image;
          }

          // Otherwise, upload the file using uploadImage
          const [downloadURL, error] = await uploadImage(
            `washingmachines/${brand}/${machineId}`,
            image,
          );
          if (error) throw error;
          if (!downloadURL) throw new Error("Failed to get download URL");
          return downloadURL;
        }),
      );
    },
    async getBrandData(brand: string): Promise<{ brandRef: any; brandData: BrandData }> {
      const brandRef = doc(db, "washingmachines", brand);
      const brandDoc = await getDoc(brandRef);

      let brandData: BrandData = {
        machines: [],
        models: [],
        types: [],
        years: [],
      };

      if (brandDoc.exists()) {
        const data = brandDoc.data();
        brandData = {
          machines: data.machines || [],
          models: data.models || [],
          types: data.types || [],
          years: data.years || [],
        };
      }

      return { brandRef, brandData };
    },
    updateBrandMetadata(brandData: BrandData, machineData: Partial<WashingMachine>) {
      if (machineData.model) brandData.models.push(machineData.model);
      if (machineData.type) brandData.types.push(machineData.type);
      if (machineData.year) brandData.years.push(machineData.year);

      // Remove duplicates
      brandData.models = [...new Set(brandData.models)];
      brandData.types = [...new Set(brandData.types)];
      brandData.years = [...new Set(brandData.years)];
    },
    async addWashingMachine(data: { files?: File[]; brand: string } & Partial<WashingMachine>) {
      const files = data.files;
      const { files: ignoreFiles, brand, ...machineData } = JSON.parse(JSON.stringify(data));
      machineData.id = crypto.randomUUID();
      machineData.createdAt = Date.now();
      machineData.updatedAt = Date.now();

      // Get or create brand document
      const { brandRef, brandData } = await this.getBrandData(brand);

      // Handle image uploads
      if (files) {
        machineData.images = await this.handleImageUploads(files, brand, machineData.id);
      }

      // Update brand metadata
      brandData.machines.push(machineData as WashingMachine);
      this.updateBrandMetadata(brandData, machineData);

      // Update brand document
      await setDoc(brandRef, brandData);

      this.selectedWashingMachine = { ...machineData, brand } as WashingMachine;
      return machineData.id;
    },
    async updateWashingMachine(
      data: {
        files?: File[];
        id: string;
        brand: string;
      } & Partial<WashingMachine>,
    ) {
      const files = data.files;
      const { files: ignoreFiles, id, brand, ...machineData } = JSON.parse(JSON.stringify(data));
      machineData.updatedAt = Date.now();

      // Get current washing machine to check if brand changed
      const currentMachine = this.washingMachines.find((w) => w.id === id);
      if (!currentMachine) throw new Error("Washing machine not found");

      const oldBrandRef = doc(db, "washingmachines", currentMachine.brand);

      // Handle image uploads
      if (files && files.length > 0) {
        const newImages = await this.handleImageUploads(files, brand, id);
        machineData.images = [...(machineData.images || []), ...newImages];
      }

      if (machineData.images && machineData.images.length === 0) {
        delete machineData.images;
      }

      // If brand changed, we need to move the machine
      if (currentMachine.brand !== brand) {
        // Remove from old brand
        const oldBrandDoc = await getDoc(oldBrandRef);
        if (oldBrandDoc.exists()) {
          const oldBrandData = oldBrandDoc.data() as BrandData;
          oldBrandData.machines = oldBrandData.machines.filter((m) => m.id !== id);
          await setDoc(oldBrandRef, oldBrandData);
        }

        // Add to new brand
        const { brandRef, brandData } = await this.getBrandData(brand);
        brandData.machines.push({ id, ...machineData } as WashingMachine);
        this.updateBrandMetadata(brandData, machineData);
        await setDoc(brandRef, brandData);
      } else {
        // Just update within the same brand
        const brandDoc = await getDoc(oldBrandRef);
        if (brandDoc.exists()) {
          const brandData = brandDoc.data() as BrandData;
          const machineIndex = brandData.machines.findIndex((m) => m.id === id);
          if (machineIndex !== -1) {
            brandData.machines[machineIndex] = {
              id,
              ...machineData,
            } as WashingMachine;
            this.updateBrandMetadata(brandData, machineData);
            await setDoc(oldBrandRef, brandData);
          }
        }
      }

      this.selectedWashingMachine = { ...machineData, brand } as WashingMachine;
    },

    async getConnectedWashingMachines(mimboxId: string): Promise<WashingMachine[]> {
      const washingMachinesQuery = query(
        collection(db, "washingmachines"),
        where("connected", "==", mimboxId),
      );
      const washingMachinesSnapshot = await getDocs(washingMachinesQuery);
      return washingMachinesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as WashingMachine[];
    },
    async deleteWashingMachine(id: string) {
      const washingMachine = this.washingMachines.find((w) => w.id === id);
      if (!washingMachine) throw new Error("Washing machine not found");

      const { brandRef, brandData } = await this.getBrandData(washingMachine.brand);
      brandData.machines = brandData.machines.filter((m) => m.id !== id);

      // Rebuild metadata from remaining machines
      brandData.models = [];
      brandData.types = [];
      brandData.years = [];

      brandData.machines.forEach((machine) => {
        if (machine.model) brandData.models.push(machine.model);
        if (machine.type) brandData.types.push(machine.type);
        if (machine.year) brandData.years.push(machine.year);
      });

      // Remove duplicates
      brandData.models = [...new Set(brandData.models)];
      brandData.types = [...new Set(brandData.types)];
      brandData.years = [...new Set(brandData.years)];

      await setDoc(brandRef, brandData);
      await this.refresh();
    },
    async getBrandsModelsTypes(snapshot?: any) {
      const washingMachinesQuery = query(collection(db, "washingmachines"));
      const washingMachinesSnapshot = snapshot ?? (await getDocs(washingMachinesQuery));

      // Brands are just the document IDs
      this.brands = washingMachinesSnapshot.docs.map((doc: any) => {
        return {
          name: doc.id,
          models: doc.data().models,
          types: doc.data().types,
        };
      });
    },
    async connectToMimbox(washingMachineId: string, mimboxId: string | undefined) {
      const washingMachine = this.washingMachines.find((w) => w.id === washingMachineId);
      if (!washingMachine) throw new Error("Washing machine not found");

      const { brandRef, brandData } = await this.getBrandData(washingMachine.brand);
      const machineIndex = brandData.machines.findIndex((m) => m.id === washingMachineId);

      if (machineIndex === -1) throw new Error("Washing machine not found in brand data");

      brandData.machines[machineIndex] = {
        ...brandData.machines[machineIndex],
        connected: mimboxId,
      };

      await setDoc(brandRef, brandData);
      await this.refresh();
    },
  },
});
